import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import isAdministrator from "../utilities/checkPrivileges";
import depProfileService from "../services/depProfileService";
import nanoCmdService from "../services/nanoCmdService";

const emptyForm = {
  profile_name: "",
  url: "",
  support_phone_number: "",
  support_email_address: "",
  department: "",
  language: "de",
  region: "DE",
  allow_pairing: true,
  is_supervised: true,
  is_mandatory: true,
  is_mdm_removable: false,
  await_device_configured: false,
  auto_advance_setup: false,
  is_multi_user: false,
  retainAppsOnWipe: false,
  configuration_web_enabled: false,
  configuration_web_url: "",
  splashMarkdown: "",
  minimum_os_version: "",
  skip_setup_items: [],
  sharedIpad: { residentUsers: "", quotaSizeMb: "" },
  postEnrollment: {
    cmdPlan: "",
    nanoCmdProfiles: [],
    runInventory: false,
  },
  macAccountSetup: {
    enabled: false,
    fullName: "Administrator",
    shortName: "admin",
    password: "",
    hidden: false,
    skipPrimarySetupAccountCreation: true,
    setPrimarySetupAccountAsRegularUser: true,
    dontAutoPopulatePrimaryAccountInfo: true,
    lockPrimaryAccountInfo: false,
    primaryAccountFullName: "",
    primaryAccountUserName: "",
    passwordSet: false,
  },
};

function isLoopbackUrl(value) {
  return /127\.0\.0|localhost|::1/i.test(String(value || ""));
}

function formFromProfile(profile, defaultUrl, defaultWebUrl) {
  const url = isLoopbackUrl(profile.url) ? (defaultUrl || "") : (profile.url || defaultUrl || "");
  const web = isLoopbackUrl(profile.configuration_web_url)
    ? (defaultWebUrl || "")
    : (profile.configuration_web_url || defaultWebUrl || "");
  return {
    ...emptyForm,
    ...profile,
    url,
    configuration_web_url: web,
    configuration_web_enabled: Boolean(profile.configuration_web_enabled ?? profile.configuration_web_url),
    splashMarkdown: profile.splashMarkdown || "",
    skip_setup_items: profile.skip_setup_items || [],
    sharedIpad: {
      residentUsers: profile.sharedIpad?.residentUsers || "",
      quotaSizeMb: profile.sharedIpad?.quotaSizeMb || "",
    },
    postEnrollment: {
      cmdPlan: profile.postEnrollment?.cmdPlan || "",
      nanoCmdProfiles: profile.postEnrollment?.nanoCmdProfiles || [],
      runInventory: Boolean(profile.postEnrollment?.runInventory),
    },
    macAccountSetup: {
      ...emptyForm.macAccountSetup,
      ...(profile.macAccountSetup || {}),
      password: "",
      passwordSet: Boolean(profile.macAccountSetup?.passwordSet),
    },
  };
}

function eventLabel(event) {
  return {
    authenticate: "Enrollment gestartet",
    token_update: "TokenUpdate",
    checkout: "CheckOut",
    dep_assign: "DEP zugewiesen",
    workflow: "Workflow",
    profile_define: "Profil definiert",
  }[event] || event;
}

export default function DepProfiles() {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const admin = isAdministrator();
  const [tab, setTab] = useState("profiles");
  const [profiles, setProfiles] = useState([]);
  const [skipItems, setSkipItems] = useState([]);
  const [defaultUrl, setDefaultUrl] = useState("");
  const [defaultWebUrl, setDefaultWebUrl] = useState("");
  const [skipSource, setSkipSource] = useState("");
  const [supervisionIdentity, setSupervisionIdentity] = useState({ configured: false });
  const [showPayload, setShowPayload] = useState(false);
  const [cmdPlans, setCmdPlans] = useState([]);
  const [nanoProfiles, setNanoProfiles] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventQuery, setEventQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, skip, plans, ncmd] = await Promise.all([
        depProfileService.list(),
        depProfileService.getSkipSetupItems().catch(() => ({ items: [], defaultEnrollUrl: "" })),
        nanoCmdService.getCmdPlans().catch(() => []),
        nanoCmdService.getProfiles().catch(() => ({ profiles: [] })),
      ]);
      setProfiles(Array.isArray(rows) ? rows : []);
      setSkipItems(skip.items || []);
      setDefaultUrl(skip.defaultEnrollUrl || "");
      setDefaultWebUrl(skip.defaultConfigurationWebUrl || "");
      setSkipSource(skip.skipKeysSource || "");
      setSupervisionIdentity(skip.supervisionIdentity || { configured: false });
      setCmdPlans(Array.isArray(plans) ? plans : []);
      setNanoProfiles(ncmd.profiles || []);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "DEP-Profile konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEvents = useCallback(async () => {
    try {
      const rows = await depProfileService.listEvents({
        serialNumber: eventQuery.trim() || undefined,
        limit: 200,
      });
      setEvents(rows);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Enrollment-Log konnte nicht geladen werden.");
    }
  }, [eventQuery]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  useEffect(() => {
    if (tab === "log") loadEvents();
    // Suche läuft über Button; nicht bei jedem Tippen neu laden.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tab only
  }, [tab]);

  const startCreate = () => {
    setEditing("new");
    setForm({ ...emptyForm, url: defaultUrl, configuration_web_url: defaultWebUrl });
  };

  const startEdit = (profile) => {
    setEditing(profile);
    setForm(formFromProfile(profile, defaultUrl, defaultWebUrl));
  };

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setPost = (key, value) => setForm((prev) => ({
    ...prev,
    postEnrollment: { ...prev.postEnrollment, [key]: value },
  }));
  const setShared = (key, value) => setForm((prev) => ({
    ...prev,
    sharedIpad: { ...prev.sharedIpad, [key]: value },
  }));
  const setMacAccount = (key, value) => setForm((prev) => {
    const macAccountSetup = { ...prev.macAccountSetup, [key]: value };
    const next = { ...prev, macAccountSetup };
    if (key === "enabled" && value) next.await_device_configured = true;
    return next;
  });

  const payloadPreview = useMemo(() => JSON.stringify({
    profile_name: form.profile_name,
    url: form.url,
    configuration_web_url: form.configuration_web_enabled ? (form.configuration_web_url || undefined) : undefined,
    allow_pairing: form.allow_pairing,
    is_supervised: form.is_supervised,
    is_mandatory: form.is_mandatory,
    is_mdm_removable: form.is_mdm_removable,
    is_multi_user: form.is_multi_user,
    support_phone_number: form.support_phone_number || undefined,
    support_email_address: form.support_email_address || undefined,
    department: form.department || undefined,
    language: form.language,
    region: form.region,
    skip_setup_items: form.skip_setup_items,
    await_device_configured: form.await_device_configured,
    auto_advance_setup: form.auto_advance_setup,
    minimum_os_version: form.minimum_os_version || undefined,
    supervising_host_certs: form.is_supervised && supervisionIdentity.configured
      ? ["<Xavier Betreuungsidentität — DER Base64>"]
      : undefined,
  }, null, 2), [form, supervisionIdentity.configured]);

  const toggleSkip = (id) => {
    setForm((prev) => {
      const has = prev.skip_setup_items.includes(id);
      return {
        ...prev,
        skip_setup_items: has
          ? prev.skip_setup_items.filter((item) => item !== id)
          : [...prev.skip_setup_items, id],
      };
    });
  };

  const toggleNanoProfile = (name) => {
    const current = form.postEnrollment.nanoCmdProfiles || [];
    setPost(
      "nanoCmdProfiles",
      current.includes(name) ? current.filter((item) => item !== name) : [...current, name]
    );
  };

  const savePayload = () => ({
    profile_name: form.profile_name,
    url: form.url,
    support_phone_number: form.support_phone_number,
    support_email_address: form.support_email_address,
    department: form.department,
    language: form.language,
    region: form.region,
    allow_pairing: form.allow_pairing,
    is_supervised: form.is_supervised,
    is_mandatory: form.is_mandatory,
    is_mdm_removable: form.is_mdm_removable,
    auto_advance_setup: form.auto_advance_setup,
    is_multi_user: form.is_multi_user,
    retainAppsOnWipe: form.retainAppsOnWipe,
    configuration_web_enabled: form.configuration_web_enabled,
    configuration_web_url: form.configuration_web_url,
    splashMarkdown: form.splashMarkdown,
    minimum_os_version: form.minimum_os_version,
    skip_setup_items: form.skip_setup_items,
    sharedIpad: form.sharedIpad,
    postEnrollment: form.postEnrollment,
    macAccountSetup: {
      enabled: Boolean(form.macAccountSetup.enabled),
      fullName: form.macAccountSetup.fullName,
      shortName: form.macAccountSetup.shortName,
      hidden: Boolean(form.macAccountSetup.hidden),
      skipPrimarySetupAccountCreation: Boolean(form.macAccountSetup.skipPrimarySetupAccountCreation),
      setPrimarySetupAccountAsRegularUser: form.macAccountSetup.setPrimarySetupAccountAsRegularUser !== false,
      dontAutoPopulatePrimaryAccountInfo: Boolean(form.macAccountSetup.dontAutoPopulatePrimaryAccountInfo),
      lockPrimaryAccountInfo: Boolean(form.macAccountSetup.lockPrimaryAccountInfo),
      primaryAccountFullName: form.macAccountSetup.primaryAccountFullName,
      primaryAccountUserName: form.macAccountSetup.primaryAccountUserName,
      ...(form.macAccountSetup.password
        ? { password: form.macAccountSetup.password }
        : {}),
    },
    await_device_configured: form.macAccountSetup.enabled ? true : form.await_device_configured,
  });

  const save = async () => {
    if (!form.profile_name.trim()) {
      toast.error("Profilname ist erforderlich.");
      return;
    }
    if (form.macAccountSetup.enabled) {
      if (!form.macAccountSetup.shortName.trim()) {
        toast.error("Kurzname für den Managed Admin ist erforderlich.");
        return;
      }
      if (!form.macAccountSetup.password && !form.macAccountSetup.passwordSet) {
        toast.error("Passwort für den Managed Admin ist erforderlich.");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = savePayload();
      if (editing === "new") {
        const created = await depProfileService.define(payload);
        toast.success(`DEP-Profil bei Apple angelegt (${created.profile_uuid}).`);
      } else {
        const saved = await depProfileService.update(editing.id || editing._id, payload);
        if (saved.appleUpdated) {
          const extra = saved.reassigned
            ? ` ${saved.reassigned} Gerät(e) auf die neue UUID umgehängt.`
            : saved.previousUuid
              ? " Geräte mit der alten UUID ggf. unter Geräte-Registrierung neu zuweisen."
              : "";
          toast.success(`DEP-Profil bei Apple aktualisiert.${extra}`);
        } else {
          toast.success("Post-Enrollment-Workflows gespeichert (Apple-Profil unverändert).");
        }
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (profile) => {
    if (!window.confirm(`Profil „${profile.profile_name || profile.profile_uuid}“ aus Xavier entfernen? Das Apple-Profil bleibt bestehen.`)) return;
    try {
      await depProfileService.remove(profile.id || profile._id);
      toast.success("Aus Xavier entfernt.");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Löschen fehlgeschlagen.");
    }
  };

  const visibleEvents = useMemo(() => events, [events]);

  if (!user) return null;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h2 className="mb-1">DEP-Profile & Enrollment</h2>
          <p className="text-muted mb-0">
            Profile für Automated Device Enrollment bei Apple anlegen. Nach dem Enrollment startet Xavier die gewählten NanoCMD-Workflows.
          </p>
          <p className="form-text mb-0 mt-2">
            Ein Gerät, das den Setup-Assistenten schon gestartet hat, behält das <strong>bereits geladene</strong> ADE-Profil.
            Speichern in Xavier ändert nur Apples Zuweisung für den <em>nächsten</em> Durchlauf.
            Neustart reicht nicht — das Gerät muss gelöscht/wiederhergestellt werden (oder per Apple Configurator), dann holt es das neue Profil.
            Steckt es schon nach MDM-Enrollment auf der Vorschaltseite, kannst du unter Geräte-Registrierung <strong>Setup fortsetzen</strong> senden, ohne zu wischen.
          </p>
        </div>
        <div className="d-flex gap-2">
          <Link to="/enroll" className="btn btn-outline-secondary btn-sm">Geräte manuell registrieren</Link>
          <Link to="/device-registrations" className="btn btn-outline-secondary btn-sm">Zur Geräte-Registrierung</Link>
        </div>
      </div>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button type="button" className={`nav-link ${tab === "profiles" ? "active" : ""}`} onClick={() => setTab("profiles")}>
            DEP-Profile
          </button>
        </li>
        <li className="nav-item">
          <button type="button" className={`nav-link ${tab === "log" ? "active" : ""}`} onClick={() => setTab("log")}>
            Enrollment-Log
          </button>
        </li>
      </ul>

      {tab === "log" && (
        <div>
          <form
            className="d-flex gap-2 mb-3"
            onSubmit={(e) => {
              e.preventDefault();
              loadEvents();
            }}
          >
            <input
              className="form-control"
              style={{ maxWidth: 260 }}
              placeholder="Seriennummer filtern"
              value={eventQuery}
              onChange={(e) => setEventQuery(e.target.value)}
            />
            <button type="submit" className="btn btn-outline-secondary">Filtern</button>
          </form>
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Zeit</th>
                  <th>Ereignis</th>
                  <th>Status</th>
                  <th>Serial</th>
                  <th>Profil</th>
                  <th>Meldung</th>
                </tr>
              </thead>
              <tbody>
                {visibleEvents.length === 0 ? (
                  <tr><td colSpan={6} className="text-muted">Noch keine Ereignisse. Nach dem nächsten Enrollment erscheinen sie hier.</td></tr>
                ) : visibleEvents.map((row) => (
                  <tr key={row._id}>
                    <td className="text-nowrap">{row.createdAt ? new Date(row.createdAt).toLocaleString() : "—"}</td>
                    <td>{eventLabel(row.event)}</td>
                    <td>
                      <span className={`badge ${row.status === "failure" ? "text-bg-danger" : row.status === "success" ? "text-bg-success" : "text-bg-secondary"}`}>
                        {row.status}
                      </span>
                    </td>
                    <td><code>{row.serialNumber || "—"}</code></td>
                    <td>{row.depProfileName || row.depProfileUuid || "—"}</td>
                    <td className="small">{row.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "profiles" && !editing && (
        <div>
          {admin && (
            <button type="button" className="btn btn-primary mb-3" onClick={startCreate}>
              Neues DEP-Profil
            </button>
          )}
          {loading ? <p className="text-muted">Lade…</p> : (
            <div className="table-responsive">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>UUID</th>
                    <th>Enrollment-URL</th>
                    <th>Min. OS</th>
                    <th>Nach Enrollment</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {profiles.length === 0 ? (
                    <tr><td colSpan={6} className="text-muted">Noch keine Profile. Bestehende UUIDs können weiter unter Geräte-Registrierung importiert werden.</td></tr>
                  ) : profiles.map((profile) => (
                    <tr key={profile.profile_uuid}>
                      <td>
                        <strong>{profile.profile_name || "—"}</strong>
                        <div className="small text-muted">
                          {profile.is_supervised ? "Supervised" : "nicht supervised"}
                          {profile.is_mandatory ? " · Pflicht" : ""}
                          {profile.supervisionHostCertsAttached ? " · Betreuungszertifikat" : ""}
                        </div>
                      </td>
                      <td><code className="small">{profile.profile_uuid}</code></td>
                      <td className="small text-break">{profile.url || "—"}</td>
                      <td>{profile.minimum_os_version || "—"}</td>
                      <td className="small">
                        {[
                          profile.postEnrollment?.cmdPlan && `cmdplan ${profile.postEnrollment.cmdPlan}`,
                          profile.postEnrollment?.nanoCmdProfiles?.length && `${profile.postEnrollment.nanoCmdProfiles.length} Profil-WF`,
                          profile.postEnrollment?.runInventory && "Inventory",
                        ].filter(Boolean).join(" · ") || "—"}
                      </td>
                      <td className="text-nowrap">
                        {admin && (
                          <>
                            <button type="button" className="btn btn-sm btn-outline-primary me-1" onClick={() => startEdit(profile)}>Bearbeiten</button>
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => remove(profile)}>Entfernen</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "profiles" && editing && (
        <div className="card">
          <div className="card-body">
            <h5 className="mb-3">{editing === "new" ? "Neues DEP-Profil" : "DEP-Profil bearbeiten"}</h5>
            {editing !== "new" && editing.profile_uuid && (
              <p className="small text-muted">
                Aktuelle Apple-UUID: <code>{editing.profile_uuid}</code>
                {editing.lastPushedAt ? ` · zuletzt an Apple: ${new Date(editing.lastPushedAt).toLocaleString()}` : ""}
                {editing.lastError ? <span className="text-danger"> · {editing.lastError}</span> : ""}
                <br />
                Nur ADE-Felder erzeugen bei Apple ein neues Profil; Xavier hängt bekannte Geräte automatisch um. Workflow-Änderungen bleiben lokal.
              </p>
            )}
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Profilname</label>
                <input className="form-control" value={form.profile_name} onChange={(e) => setField("profile_name", e.target.value)} />
              </div>
              <div className="col-md-6">
                <label className="form-label">Enrollment-URL</label>
                <input className="form-control" value={form.url} onChange={(e) => setField("url", e.target.value)} placeholder={defaultUrl || "https://mdm.example.de/mdm_enroll"} />
                <div className="form-text">
                  Öffentliche Enrollment-URL der Geräte (Xavier, <code>/mdm_enroll</code>). Vorbelegt aus <code>MDM_ENROLL_URL</code> bzw. der Xavier-Adresse.
                  Das Profil enthält SCEP, MDM und optional das Signatur-Stammzertifikat. SCEP und ServerURL zeigen auf CA bzw. NanoHUB, nicht auf diese Oberfläche.
                </div>
              </div>
              <div className="col-md-4">
                <label className="form-label">Support-Telefon</label>
                <input className="form-control" value={form.support_phone_number} onChange={(e) => setField("support_phone_number", e.target.value)} />
              </div>
              <div className="col-md-4">
                <label className="form-label">Support-E-Mail</label>
                <input className="form-control" value={form.support_email_address} onChange={(e) => setField("support_email_address", e.target.value)} />
              </div>
              <div className="col-md-4">
                <label className="form-label">Abteilung</label>
                <input className="form-control" value={form.department} onChange={(e) => setField("department", e.target.value)} />
              </div>
              <div className="col-md-3">
                <label className="form-label">Sprache</label>
                <input className="form-control" value={form.language} onChange={(e) => setField("language", e.target.value)} />
              </div>
              <div className="col-md-3">
                <label className="form-label">Region</label>
                <input className="form-control" value={form.region} onChange={(e) => setField("region", e.target.value)} />
              </div>
              <div className="col-md-3">
                <label className="form-label">Mindest-OS vor Enrollment</label>
                <input className="form-control" value={form.minimum_os_version} onChange={(e) => setField("minimum_os_version", e.target.value)} placeholder="z. B. 18.0" />
              </div>
            </div>

            <div className="card card-body mt-3">
              <label className="form-check mb-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={Boolean(form.configuration_web_enabled)}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setField("configuration_web_enabled", enabled);
                    if (enabled && !form.await_device_configured) setField("await_device_configured", true);
                  }}
                />
                <span className="form-check-label"><strong>Vorschaltseite im Setup</strong> (optional, für Benutzerzuordnung)</span>
              </label>
              <p className="form-text mt-0">
                Ausgeschaltet bleibt ADE ohne Extra-Schritt. Eingeschaltet öffnet Apple zuerst
                {' '}<code>/api/enroll/start</code> (Geräteidentität aus dem Setup-Assistenten) und dann{' '}
                <Link to="/enroll">/enroll</Link>.
                Gerätebenutzer (nicht Console) melden sich an; die Zuordnung gilt automatisch für genau dieses Gerät, ohne Seriennummer einzugeben.
              </p>
              {form.configuration_web_enabled && (
                <>
                  <label className="form-label">Konfigurations-Web-URL</label>
                  <input className="form-control mb-2" value={form.configuration_web_url} onChange={(e) => setField("configuration_web_url", e.target.value)} placeholder={defaultWebUrl || "https://xavier.example.de/api/enroll/start"} />
                  <label className="form-label">Text der Vorschaltseite (Markdown)</label>
                  <textarea
                    className="form-control font-monospace small"
                    rows={8}
                    value={form.splashMarkdown}
                    onChange={(e) => setField("splashMarkdown", e.target.value)}
                    placeholder={"# Willkommen\nBitte mit deinem Schulkonto anmelden.\n\n![Logo](https://example.com/logo.png)"}
                  />
                  <div className="form-text">Überschriften, **fett**, Listen, Links und Bilder per `![alt](https://…)`.</div>
                  <div className="form-text">
                    Änderung gilt für Geräte, die ADE <em>neu</em> starten. Ein Gerät mitten im Setup hat die alte URL bereits.
                  </div>
                </>
              )}
            </div>

            <div className="d-flex flex-wrap gap-3 mt-3">
              {[
                ["is_supervised", "Supervised"],
                ["is_mandatory", "Pflichtprofil"],
                ["is_mdm_removable", "MDM entfernbar"],
                ["allow_pairing", "Pairing erlaubt"],
                ["await_device_configured", "Auf DeviceConfigured warten"],
                ["auto_advance_setup", "Auto-Advance (tvOS)"],
                ["is_multi_user", "Shared iPad"],
                ["retainAppsOnWipe", "Apps bei Wipe/Return-to-Service belassen"],
              ].map(([key, label]) => (
                <label key={key} className="form-check">
                  <input type="checkbox" className="form-check-input" checked={Boolean(form[key])} onChange={(e) => setField(key, e.target.checked)} />
                  <span className="form-check-label">{label}</span>
                </label>
              ))}
            </div>

            <p className="form-text mt-2 mb-0">
              <strong>Auto-Advance (tvOS):</strong> Apple TV geht ohne Nutzeraktion durch den Setup-Assistenten (typisch per Ethernet).
              <br />
              <strong>Apps belassen:</strong> setzt ADE <code>is_return_to_service</code> (und erzwingt Await Configuration).
              Für Erase mit App-Preservation braucht Xavier zusätzlich den BootstrapToken vom Gerät.
            </p>

            <h6 className="mt-4">macOS: Managed Admin (AccountConfiguration)</h6>
            <p className="form-text">
              Erzeugt beim ADE-Enrollment einen lokalen Admin im Setup Assistant (vor DeviceConfigured).
              Aktiviert automatisch „Auf DeviceConfigured warten“. Gilt nur für Macs mit diesem DEP-Profil.
            </p>
            <label className="form-check mb-2">
              <input
                type="checkbox"
                className="form-check-input"
                checked={Boolean(form.macAccountSetup.enabled)}
                onChange={(e) => setMacAccount("enabled", e.target.checked)}
              />
              <span className="form-check-label">Managed Admin im Setup anlegen</span>
            </label>
            {form.macAccountSetup.enabled && (
              <div className="row g-3 mb-2">
                <div className="col-md-4">
                  <label className="form-label">Anzeigename</label>
                  <input className="form-control" value={form.macAccountSetup.fullName} onChange={(e) => setMacAccount("fullName", e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Kurzname (Account)</label>
                  <input className="form-control" value={form.macAccountSetup.shortName} onChange={(e) => setMacAccount("shortName", e.target.value)} placeholder="admin" />
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Passwort{form.macAccountSetup.passwordSet ? " (leer = unverändert)" : ""}
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    autoComplete="new-password"
                    value={form.macAccountSetup.password}
                    onChange={(e) => setMacAccount("password", e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-check">
                    <input type="checkbox" className="form-check-input" checked={Boolean(form.macAccountSetup.hidden)} onChange={(e) => setMacAccount("hidden", e.target.checked)} />
                    <span className="form-check-label">Admin-Account versteckt</span>
                  </label>
                  <label className="form-check">
                    <input type="checkbox" className="form-check-input" checked={Boolean(form.macAccountSetup.skipPrimarySetupAccountCreation)} onChange={(e) => setMacAccount("skipPrimarySetupAccountCreation", e.target.checked)} />
                    <span className="form-check-label">Primäre Account-Erstellung überspringen</span>
                  </label>
                  <label className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={form.macAccountSetup.setPrimarySetupAccountAsRegularUser !== false}
                      onChange={(e) => setMacAccount("setPrimarySetupAccountAsRegularUser", e.target.checked)}
                      disabled={Boolean(form.macAccountSetup.skipPrimarySetupAccountCreation)}
                    />
                    <span className="form-check-label">Primär-Account als Standard-User (kein Admin)</span>
                  </label>
                  <label className="form-check">
                    <input type="checkbox" className="form-check-input" checked={Boolean(form.macAccountSetup.dontAutoPopulatePrimaryAccountInfo)} onChange={(e) => setMacAccount("dontAutoPopulatePrimaryAccountInfo", e.target.checked)} />
                    <span className="form-check-label">Primär-Account nicht vorausfüllen</span>
                  </label>
                </div>
                <div className="col-12 form-text">
                  Wenn der Setup-Assistent einen lokalen Benutzer anlegt: „als Standard-User“ verhindert Admin-Rechte
                  (Managed Admin bleibt Admin). „Überspringen“ = nur Managed Admin, kein Primär-Account im Setup.
                  Nach Speichern Await Configuration an Apple pushen. Ein anderer lokaler Admin kann den Managed Admin
                  unter macOS trotzdem ändern — MDM kann das Passwort per <code>SetAutoAdminPassword</code> wiederherstellen,
                  aber nicht vollständig gegen Admin-Admins sperren.
                </div>
              </div>
            )}

            <div className={`alert small mt-3 mb-0 ${supervisionIdentity.configured && form.is_supervised ? "alert-success" : "alert-warning"}`}>
              {supervisionIdentity.configured && form.is_supervised ? (
                <>
                  Beim Speichern sendet Xavier die Betreuungsidentität als <code>supervising_host_certs</code> an Apple
                  {supervisionIdentity.subject ? <> ({supervisionIdentity.subject})</> : null}.
                  Geräte übernehmen das erst bei neuem ADE-Enrollment (Wipe / Reset).
                  {editing !== "new" && !editing?.supervisionHostCertsAttached ? (
                    <> <strong>Dieses Profil hat das Zertifikat noch nicht an Apple — bitte speichern.</strong></>
                  ) : null}
                </>
              ) : (
                <>
                  Ohne Betreuungsidentität in den{" "}
                  <Link to="/settings">Einstellungen</Link> (bzw. ohne „Supervised“) enthält das DEP-Profil
                  kein <code>supervising_host_certs</code> — Configurator verlangt dann weiterhin die Freigabe am Gerät.
                </>
              )}
            </div>

            {form.is_multi_user && (
              <div className="row g-3 mt-2">
                <div className="col-md-6">
                  <label className="form-label">Shared iPad: residente Nutzer</label>
                  <input type="number" min="1" className="form-control" value={form.sharedIpad.residentUsers} onChange={(e) => setShared("residentUsers", e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">oder Speicherkontingent pro Nutzer (MB)</label>
                  <input type="number" min="1" className="form-control" value={form.sharedIpad.quotaSizeMb} onChange={(e) => setShared("quotaSizeMb", e.target.value)} />
                </div>
                <div className="col-12 form-text">
                  <code>is_multi_user</code> geht an Apple. Quota/Nutzerzahl sendet Xavier nach dem Enrollment per MDM-Settings (<code>SharedDeviceConfiguration</code>). Üblicherweise nur eines von beiden setzen.
                </div>
              </div>
            )}

            <h6 className="mt-4">Setup-Assistent überspringen</h6>
            <div className="d-flex gap-2 mb-2">
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setField("skip_setup_items", skipItems.map((item) => item.id))}>
                Alle auswählen
              </button>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setField("skip_setup_items", [])}>
                Alle abwählen
              </button>
            </div>
            <p className="form-text">
              Quelle: Apple SkipKeys{skipSource ? <> — <a href={skipSource} target="_blank" rel="noreferrer">device-management/other/skipkeys.yaml</a></> : null}
            </p>
            <div className="row">
              {skipItems.map((item) => (
                <div className="col-md-4 col-lg-3" key={item.id}>
                  <label className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={form.skip_setup_items.includes(item.id)}
                      onChange={() => toggleSkip(item.id)}
                    />
                    <span className="form-check-label small">{item.label}</span>
                  </label>
                </div>
              ))}
            </div>

            <h6 className="mt-4">Nach dem Enrollment (NanoCMD)</h6>
            <p className="small text-muted">
              Läuft automatisch beim ersten TokenUpdate. <code>io.micromdm.wf.cmdplan.v1</code> startet einen Command Plan,
              <code> io.micromdm.wf.profile.v1</code> spielt NanoCMD-Profile aus.
            </p>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Command Plan</label>
                <select
                  className="form-select"
                  value={form.postEnrollment.cmdPlan}
                  onChange={(e) => setPost("cmdPlan", e.target.value)}
                >
                  <option value="">keiner</option>
                  {cmdPlans.map((plan) => (
                    <option key={plan.name} value={plan.name}>{plan.name}</option>
                  ))}
                </select>
                {!cmdPlans.length && (
                  <div className="form-text">Keine Pläne. <code>NANOCMD_CMDPLANS</code> auf dem Server setzen (z. B. enrollment).</div>
                )}
              </div>
              <div className="col-md-6">
                <label className="form-check mt-4">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={form.postEnrollment.runInventory}
                    onChange={(e) => setPost("runInventory", e.target.checked)}
                  />
                  <span className="form-check-label">Inventory-Workflow starten</span>
                </label>
              </div>
              <div className="col-12">
                <label className="form-label">NanoCMD-Profile (io.micromdm.wf.profile.v1)</label>
                <div className="border rounded p-2" style={{ maxHeight: 180, overflow: "auto" }}>
                  {nanoProfiles.length === 0 ? (
                    <span className="text-muted small">Keine NanoCMD-Profile gefunden.</span>
                  ) : nanoProfiles.map((profile) => (
                    <label key={profile.name} className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={form.postEnrollment.nanoCmdProfiles.includes(profile.name)}
                        onChange={() => toggleNanoProfile(profile.name)}
                      />
                      <span className="form-check-label small">{profile.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-4">
              <button type="button" className="btn btn-sm btn-outline-secondary mb-2" onClick={() => setShowPayload((prev) => !prev)}>
                {showPayload ? "Profiltext ausblenden" : "Fertiges Profil als Text"}
              </button>
              {showPayload && (
                <pre className="bg-light border rounded p-2 small" style={{ maxHeight: 280, overflow: "auto" }}>{payloadPreview}</pre>
              )}
            </div>

            <div className="d-flex gap-2 mt-3">
              <button type="button" className="btn btn-primary" disabled={saving || !admin} onClick={save}>
                {saving ? "Speichere…" : "Speichern"}
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setEditing(null)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
